import React, { useEffect, useState } from 'react';
import { useTenantUserInfo } from '../../context/userInfoProvider';

const pageContainer: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: React.CSSProperties = {
  width: '100%',
  maxWidth: 900,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const headerRow: React.CSSProperties = {
  marginBottom: 16,
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 14,
  opacity: 0.8,
  marginTop: 4,
};

const sectionCard: React.CSSProperties = {
  backgroundColor: 'rgba(23, 8, 36, 0.9)',
  borderRadius: 12,
  padding: 16,
  border: '1px solid rgba(255,255,255,0.05)',
  marginBottom: 12,
};

const sectionHeaderRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 8,
};

const sectionTitle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
};

const editButton: React.CSSProperties = {
  borderRadius: 999,
  padding: '4px 10px',
  backgroundColor: '#7B1FA2',
  border: 'none',
  color: '#fff',
  fontSize: 12,
  cursor: 'pointer',
};

const fieldGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 12,
};

const fieldCard: React.CSSProperties = {
  backgroundColor: 'rgba(39, 20, 63, 0.9)',
  borderRadius: 8,
  padding: 10,
};

const fieldLabel: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.7,
  opacity: 0.7,
  marginBottom: 4,
};

const fieldValue: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  borderRadius: 6,
  border: '1px solid #4826A0',
  padding: '6px 8px',
  backgroundColor: 'rgba(10, 4, 18, 0.9)',
  color: '#F5F5FA',
  fontSize: 14,
  boxSizing: 'border-box',
};

const actionRow: React.CSSProperties = {
  marginTop: 12,
  display: 'flex',
  gap: 8,
};

const primaryButton: React.CSSProperties = {
  ...editButton,
  padding: '6px 14px',
};

const secondaryButton: React.CSSProperties = {
  ...editButton,
  padding: '6px 14px',
  backgroundColor: 'transparent',
  border: '1px solid rgba(255,255,255,0.4)',
};

const noteText: React.CSSProperties = {
  fontSize: 12,
  opacity: 0.8,
  marginTop: 12,
};

const statusText: React.CSSProperties = {
  fontSize: 12,
  marginTop: 8,
};

const TenantAccountPage: React.FC = () => {
  const {
    fetchData,
    loading,
    email,
    name,
    phone,
    addressLine1,
    addressLine2,
    city,
    state,
    zip,
    specialInstructions,
    building,
    parking,
    updateUserName,
    updateUserPhone,
    updateUserAddress,
  } = useTenantUserInfo();

  const [localLoading, setLocalLoading] = useState(true);

  // editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);

  // local form state
  const [nameInput, setNameInput] = useState(name);
  const [phoneInput, setPhoneInput] = useState(phone);
  const [addressInput, setAddressInput] = useState({
    addressLine1,
    addressLine2,
    city,
    state,
    zip,
    specialInstructions,
    building,
    parking,
  });

  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLocalLoading(true);
      await fetchData();
      setLocalLoading(false);
    };
    load();
  }, [fetchData]);

  // keep local form in sync when context changes
  useEffect(() => {
    setNameInput(name);
  }, [name]);

  useEffect(() => {
    setPhoneInput(phone);
  }, [phone]);

  useEffect(() => {
    setAddressInput({
      addressLine1,
      addressLine2,
      city,
      state,
      zip,
      specialInstructions,
      building,
      parking,
    });
  }, [
    addressLine1,
    addressLine2,
    city,
    state,
    zip,
    specialInstructions,
    building,
    parking,
  ]);

  const isBusy = loading || localLoading || saving;

  const handleSaveName = async () => {
    try {
      setSaving(true);
      setStatus(null);
      await updateUserName(email, nameInput.trim());
      setStatus('Name updated.');
      setIsEditingName(false);
    } catch (err) {
      console.error(err);
      setStatus('There was a problem saving your name.');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePhone = async () => {
    try {
      setSaving(true);
      setStatus(null);
      await updateUserPhone(email, phoneInput.trim());
      setStatus('Phone updated.');
      setIsEditingPhone(false);
    } catch (err) {
      console.error(err);
      setStatus('There was a problem saving your phone number.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAddress = async () => {
    try {
      const {
        addressLine1: l1,
        city: c,
        state: st,
        zip: z,
        building: b,
        parking: p,
      } = addressInput;

      if (!l1 || !c || !st || !z || !b || !p) {
        setStatus('Please fill in all required address fields.');
        return;
      }

      setSaving(true);
      setStatus(null);
      await updateUserAddress(email, {
        ...addressInput,
        zip: z,
      });
      setStatus('Address updated.');
      setIsEditingAddress(false);
    } catch (err) {
      console.error(err);
      setStatus('There was a problem saving your address.');
    } finally {
      setSaving(false);
    }
  };

  const renderStatus = () =>
    status ? (
      <div style={statusText}>
        {status}
      </div>
    ) : null;

  if (loading && localLoading) {
    return (
      <div style={pageContainer}>
        <div style={shellCard}>
          <p>Loading account information…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div style={headerRow}>
          <h1 style={titleStyle}>Account information</h1>
          <p style={subtitleStyle}>
            View and update the contact details associated with your Bins account.
          </p>
        </div>

        {/* Name section */}
        <section style={sectionCard}>
          <div style={sectionHeaderRow}>
            <div style={sectionTitle}>Name &amp; email</div>
            {!isEditingName && (
              <button
                style={editButton}
                onClick={() => setIsEditingName(true)}
                disabled={isBusy}
              >
                Edit
              </button>
            )}
          </div>
          <div style={fieldGrid}>
            <div style={fieldCard}>
              <div style={fieldLabel}>Name</div>
              {isEditingName ? (
                <input
                  style={inputStyle}
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  disabled={saving}
                />
              ) : (
                <div style={fieldValue}>{name || '—'}</div>
              )}
            </div>

            <div style={fieldCard}>
              <div style={fieldLabel}>Email</div>
              <div style={fieldValue}>{email || '—'}</div>
            </div>
          </div>

          {isEditingName && (
            <div style={actionRow}>
              <button
                style={primaryButton}
                onClick={handleSaveName}
                disabled={saving}
              >
                Save
              </button>
              <button
                style={secondaryButton}
                onClick={() => {
                  setIsEditingName(false);
                  setNameInput(name);
                }}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          )}
        </section>

        {/* Phone section */}
        <section style={sectionCard}>
          <div style={sectionHeaderRow}>
            <div style={sectionTitle}>Phone</div>
            {!isEditingPhone && (
              <button
                style={editButton}
                onClick={() => setIsEditingPhone(true)}
                disabled={isBusy}
              >
                Edit
              </button>
            )}
          </div>
          <div style={fieldGrid}>
            <div style={fieldCard}>
              <div style={fieldLabel}>Phone</div>
              {isEditingPhone ? (
                <input
                  style={inputStyle}
                  value={phoneInput}
                  onChange={e => setPhoneInput(e.target.value)}
                  disabled={saving}
                />
              ) : (
                <div style={fieldValue}>{phone || '—'}</div>
              )}
            </div>
          </div>
          {isEditingPhone && (
            <div style={actionRow}>
              <button
                style={primaryButton}
                onClick={handleSavePhone}
                disabled={saving}
              >
                Save
              </button>
              <button
                style={secondaryButton}
                onClick={() => {
                  setIsEditingPhone(false);
                  setPhoneInput(phone);
                }}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          )}
        </section>

        {/* Address section */}
        <section style={sectionCard}>
          <div style={sectionHeaderRow}>
            <div style={sectionTitle}>Address</div>
            {!isEditingAddress && (
              <button
                style={editButton}
                onClick={() => setIsEditingAddress(true)}
                disabled={isBusy}
              >
                Edit
              </button>
            )}
          </div>
          <div style={fieldGrid}>
            <div style={fieldCard}>
              <div style={fieldLabel}>Address line 1</div>
              {isEditingAddress ? (
                <input
                  style={inputStyle}
                  value={addressInput.addressLine1}
                  onChange={e =>
                    setAddressInput(prev => ({
                      ...prev,
                      addressLine1: e.target.value,
                    }))
                  }
                  disabled={saving}
                />
              ) : (
                <div style={fieldValue}>{addressLine1 || '—'}</div>
              )}
            </div>

            <div style={fieldCard}>
              <div style={fieldLabel}>Address line 2</div>
              {isEditingAddress ? (
                <input
                  style={inputStyle}
                  value={addressInput.addressLine2}
                  onChange={e =>
                    setAddressInput(prev => ({
                      ...prev,
                      addressLine2: e.target.value,
                    }))
                  }
                  disabled={saving}
                />
              ) : (
                <div style={fieldValue}>{addressLine2 || '—'}</div>
              )}
            </div>

            <div style={fieldCard}>
              <div style={fieldLabel}>City</div>
              {isEditingAddress ? (
                <input
                  style={inputStyle}
                  value={addressInput.city}
                  onChange={e =>
                    setAddressInput(prev => ({
                      ...prev,
                      city: e.target.value,
                    }))
                  }
                  disabled={saving}
                />
              ) : (
                <div style={fieldValue}>{city || '—'}</div>
              )}
            </div>

            <div style={fieldCard}>
              <div style={fieldLabel}>State</div>
              {isEditingAddress ? (
                <input
                  style={inputStyle}
                  value={addressInput.state}
                  onChange={e =>
                    setAddressInput(prev => ({
                      ...prev,
                      state: e.target.value,
                    }))
                  }
                  disabled={saving}
                />
              ) : (
                <div style={fieldValue}>{state || '—'}</div>
              )}
            </div>

            <div style={fieldCard}>
              <div style={fieldLabel}>ZIP</div>
              {isEditingAddress ? (
                <input
                  style={inputStyle}
                  value={addressInput.zip}
                  onChange={e =>
                    setAddressInput(prev => ({
                      ...prev,
                      zip: e.target.value,
                    }))
                  }
                  disabled={saving}
                />
              ) : (
                <div style={fieldValue}>{zip || '—'}</div>
              )}
            </div>

            <div style={fieldCard}>
              <div style={fieldLabel}>Special instructions</div>
              {isEditingAddress ? (
                <input
                  style={inputStyle}
                  value={addressInput.specialInstructions}
                  onChange={e =>
                    setAddressInput(prev => ({
                      ...prev,
                      specialInstructions: e.target.value,
                    }))
                  }
                  disabled={saving}
                />
              ) : (
                <div style={fieldValue}>
                  {specialInstructions || 'No special instructions on file.'}
                </div>
              )}
            </div>

            <div style={fieldCard}>
              <div style={fieldLabel}>Building type</div>
              {isEditingAddress ? (
                <input
                  style={inputStyle}
                  value={addressInput.building}
                  onChange={e =>
                    setAddressInput(prev => ({
                      ...prev,
                      building: e.target.value,
                    }))
                  }
                  disabled={saving}
                  placeholder="House / Apartment / …"
                />
              ) : (
                <div style={fieldValue}>{building || '—'}</div>
              )}
            </div>

            <div style={fieldCard}>
              <div style={fieldLabel}>Where to park</div>
              {isEditingAddress ? (
                <input
                  style={inputStyle}
                  value={addressInput.parking}
                  onChange={e =>
                    setAddressInput(prev => ({
                      ...prev,
                      parking: e.target.value,
                    }))
                  }
                  disabled={saving}
                  placeholder="Driveway / Parking lot / …"
                />
              ) : (
                <div style={fieldValue}>{parking || '—'}</div>
              )}
            </div>
          </div>

          {isEditingAddress && (
            <div style={actionRow}>
              <button
                style={primaryButton}
                onClick={handleSaveAddress}
                disabled={saving}
              >
                Save
              </button>
              <button
                style={secondaryButton}
                onClick={() => {
                  setIsEditingAddress(false);
                  setAddressInput({
                    addressLine1,
                    addressLine2,
                    city,
                    state,
                    zip,
                    specialInstructions,
                    building,
                    parking,
                  });
                }}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          )}

          <p style={noteText}>
            Updating your address helps drivers find you more easily. If you move
            outside a supported area, please contact Bins support to confirm
            service availability.
          </p>
        </section>

        {renderStatus()}
      </div>
    </div>
  );
};

export default TenantAccountPage;
